/**
 * Pagina /superadmin/entes-externos — Sprint 18 P14.
 *
 * Mantenedor de entes externos al barrio a los que se asignan recursos
 * sin cobro (CR citofonia porton, CR camion basura municipal, llavero
 * cartero).
 *
 * Doctrina:
 * - D-P14-3: soft-delete (campo `activo`). NO borrado fisico.
 * - D-P14-4: asignacion de recursos a ente externo es inmutable. Para
 *   "quitar" un recurso, se da de baja completo (estado terminal) y
 *   se asigna otro.
 * - D-P14-5: edicion solo super_admin desde aqui. Lectura tambien en
 *   `/tesoreria/entes-externos` (otro frontend).
 */
import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react';
import { ApiError } from '../../lib/api/client';
import {
  actualizarEnteExterno,
  crearEnteExterno,
  crearRecursoExternoDirecto,
  listEntesExternos,
  listRecursosDeEnteExterno,
  type EnteExternoRead,
  type RecursoDeEnteExternoRead,
  type TipoRecursoEnteExterno,
} from '../../lib/api/entes_externos';
import { Button } from '../../components/ui/Button';
import { ActionButton } from '../../components/ui/ActionButton';
import {
  Table,
  TBody,
  THead,
  Th,
  Tr,
  Td,
} from '../../components/ui/DataTable';
import { PageHeader } from '../../components/layout/PageHeader';
import { InputFecha } from '../../components/ui/InputFecha';
import { hoy } from '../../lib/tiempo';
import { actualizarRecurso } from '../../lib/api/recursos';
import { RECURSO_TIPO_LABEL, etiquetaTipoRecurso } from '../../lib/labels';

function formatCLP(monto: number): string {
  return `$${monto.toLocaleString('es-CL')}`;
}

function mensajeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body;
    if (body && typeof body === 'object' && 'detail' in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === 'string') return detail;
    }
    return `Error ${err.status}`;
  }
  return fallback;
}

// ============================================================
// Modal: Crear / Editar ente externo
// ============================================================

interface ModalFormProps {
  inicial: EnteExternoRead | null;
  onCerrar: () => void;
  onGuardado: () => void;
}

function ModalForm({ inicial, onCerrar, onGuardado }: ModalFormProps) {
  const esEdicion = inicial !== null;
  const [nombre, setNombre] = useState(inicial?.nombre ?? '');
  const [contacto, setContacto] = useState(inicial?.contacto ?? '');
  const [notas, setNotas] = useState(inicial?.notas ?? '');
  const [activo, setActivo] = useState(inicial?.activo ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido = nombre.trim().length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (esEdicion && inicial) {
        await actualizarEnteExterno(inicial.id, {
          nombre: nombre.trim(),
          contacto: contacto.trim() || null,
          notas: notas.trim() || null,
          activo,
        });
      } else {
        await crearEnteExterno({
          nombre: nombre.trim(),
          contacto: contacto.trim() || null,
          notas: notas.trim() || null,
        });
      }
      onGuardado();
    } catch (err) {
      setError(mensajeError(err, 'Error de red al guardar.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4"
      >
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            {esEdicion ? 'Editar ente externo' : 'Nuevo ente externo'}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Nombre <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            maxLength={150}
            disabled={submitting}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            required
            autoFocus
          />
        </label>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Contacto operativo (opcional)
          </span>
          <input
            type="text"
            value={contacto}
            onChange={(e) => setContacto(e.target.value)}
            disabled={submitting}
            placeholder="Ej: Conductor camion +56987654321 / cartero@correos.cl"
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">Notas (opcional)</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            disabled={submitting}
            rows={3}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>

        {esEdicion && (
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              disabled={submitting}
            />
            <span>
              Activo
              <span className="block text-xs text-text-muted">
                Desactivar oculta el ente del listado por defecto. Los
                recursos asignados quedan visibles en historial.
              </span>
            </span>
          </label>
        )}

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : esEdicion ? 'Guardar' : 'Crear'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Modal: Crear recurso directo (camino Y, para los 3 historicos)
// ============================================================

interface ModalRecursoDirectoProps {
  ente: EnteExternoRead;
  onCerrar: () => void;
  onGuardado: () => void;
}

function ModalRecursoDirecto({
  ente,
  onCerrar,
  onGuardado,
}: ModalRecursoDirectoProps) {
  const [tipo, setTipo] = useState<TipoRecursoEnteExterno>('control_remoto');
  const [identificador, setIdentificador] = useState('');
  const [costo, setCosto] = useState('');
  const [entregadoAt, setEntregadoAt] = useState(
    hoy(),
  );
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const costoNum = Number.parseInt(costo || '0', 10);
  const valido =
    identificador.trim().length > 0 &&
    costoNum > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(entregadoAt);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await crearRecursoExternoDirecto(ente.id, {
        tipo,
        identificador: identificador.trim(),
        costo_reposicion: costoNum,
        entregado_at: entregadoAt,
        notas: notas.trim() || null,
      });
      onGuardado();
    } catch (err) {
      setError(mensajeError(err, 'Error de red al crear el recurso.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4"
      >
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Registrar recurso histórico
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <p className="text-xs text-text-secondary bg-orange/[0.08] border-l-4 border-orange p-3 rounded-sm">
          Crea un recurso ya entregado al ente externo{' '}
          <strong>{ente.nombre}</strong>, sin tocar el inventario. Usar
          para registrar recursos históricos que ya existen físicamente.
          Para donar un recurso nuevo del stock disponible, el flujo
          (camino X) está en el backend; UI dedicada queda como cabo.
        </p>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">Tipo</span>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoRecursoEnteExterno)}
            disabled={submitting}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          >
            <option value="control_remoto">
              {RECURSO_TIPO_LABEL.control_remoto}
            </option>
            <option value="llavero">{RECURSO_TIPO_LABEL.llavero}</option>
            <option value="otro">{RECURSO_TIPO_LABEL.otro}</option>
          </select>
        </label>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Identificador <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value)}
            maxLength={50}
            disabled={submitting}
            placeholder="Ej: CR-MUNI-01 / LL-CARTERO-01"
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            required
          />
        </label>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Costo de reposición (CLP) <span className="text-state-danger">*</span>
          </span>
          <input
            type="number"
            min={1}
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
            disabled={submitting}
            placeholder="15000"
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            required
          />
        </label>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Fecha de entrega <span className="text-state-danger">*</span>
          </span>
          <InputFecha
            value={entregadoAt}
            onChange={setEntregadoAt}
            disabled={submitting}
            max={hoy()}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            requerido
          />
        </label>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">Notas (opcional)</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            disabled={submitting}
            rows={2}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Creando…' : 'Crear recurso'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Modal: Ver recursos del ente
// ============================================================

interface ModalRecursosProps {
  ente: EnteExternoRead;
  onCerrar: () => void;
}

function ModalRecursos({ ente, onCerrar }: ModalRecursosProps) {
  const [recursos, setRecursos] = useState<RecursoDeEnteExternoRead[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  // Edición inline del serial RFID (solo llaveros).
  const [editId, setEditId] = useState<string | null>(null);
  const [serialDraft, setSerialDraft] = useState('');
  const [guardando, setGuardando] = useState(false);

  function recargar() {
    listRecursosDeEnteExterno(ente.id)
      .then(setRecursos)
      .catch((err) => setError(mensajeError(err, 'Error de red.')));
  }

  useEffect(() => {
    recargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ente.id]);

  function iniciarEdicion(r: RecursoDeEnteExternoRead) {
    setEditId(r.id);
    setSerialDraft(r.serial_rfid ?? '');
    setError(null);
  }

  async function guardarSerial(r: RecursoDeEnteExternoRead) {
    const serial = serialDraft.trim();
    if (!serial || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      await actualizarRecurso(r.id, { serial_rfid: serial });
      setEditId(null);
      setSerialDraft('');
      recargar();
    } catch (err) {
      setError(mensajeError(err, 'Error al guardar el serial.'));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Recursos de {ente.nombre}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {recursos === null && !error && (
          <p className="text-sm text-text-muted">Cargando…</p>
        )}
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        {recursos !== null && recursos.length === 0 && (
          <p className="text-sm text-text-muted">
            Este ente no tiene recursos asignados.
          </p>
        )}
        {recursos !== null && recursos.length > 0 && (
          <Table>
            <THead>
              <Th>Tipo</Th>
              <Th>Identificador</Th>
              <Th>Serial RFID</Th>
              <Th>Costo</Th>
              <Th>Entregado</Th>
              <Th>Estado</Th>
            </THead>
            <TBody>
              {recursos.map((r) => (
                <Tr key={r.id}>
                  <Td>{etiquetaTipoRecurso(r.tipo)}</Td>
                  <Td className="font-mono">{r.identificador}</Td>
                  <Td>
                    {r.tipo !== 'llavero' ? (
                      <span className="text-text-muted">—</span>
                    ) : editId === r.id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={serialDraft}
                          onChange={(e) => setSerialDraft(e.target.value)}
                          maxLength={50}
                          placeholder="Serial RFID"
                          autoFocus
                          className="w-28 bg-bg-sunken border-[0.5px] border-white/15 rounded px-1.5 py-0.5 text-text-primary font-mono text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => guardarSerial(r)}
                          disabled={!serialDraft.trim() || guardando}
                          className="text-state-success hover:underline text-xs disabled:opacity-50"
                        >
                          {guardando ? '…' : 'Guardar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditId(null)}
                          disabled={guardando}
                          className="text-text-muted hover:underline text-xs"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-text-secondary">
                          {r.serial_rfid ?? '— sin serial —'}
                        </span>
                        <button
                          type="button"
                          onClick={() => iniciarEdicion(r)}
                          className="text-orange hover:underline text-xs"
                        >
                          {r.serial_rfid ? 'Editar' : 'Agregar'}
                        </button>
                      </div>
                    )}
                  </Td>
                  <Td>{formatCLP(r.costo_reposicion)}</Td>
                  <Td>{r.entregado_at}</Td>
                  <Td>{r.estado}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Página principal
// ============================================================

export function EntesExternosPage() {
  const [entes, setEntes] = useState<EnteExternoRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [editando, setEditando] = useState<EnteExternoRead | null>(null);
  const [creando, setCreando] = useState(false);
  const [recursosDe, setRecursosDe] = useState<EnteExternoRead | null>(null);
  const [recursoDirectoDe, setRecursoDirectoDe] =
    useState<EnteExternoRead | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listEntesExternos({ incluir_inactivos: incluirInactivos })
      .then((data) => {
        if (!cancelado) setEntes(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(mensajeError(err, 'Error de red al cargar.'));
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [incluirInactivos, refreshKey]);

  const ordenados = useMemo(
    () =>
      [...entes].sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }),
      ),
    [entes],
  );

  return (
    <div className="px-8 py-6 space-y-5">
      <PageHeader
        title="Entes externos"
        description="Entidades externas al barrio (citofonía, basura, correos) a las que se entregan recursos sin cobro. Donación operativa."
        action={
          <Button variant="primary" onClick={() => setCreando(true)}>
            + Nuevo ente externo
          </Button>
        }
      />

      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={incluirInactivos}
          onChange={(e) => setIncluirInactivos(e.target.checked)}
        />
        <span>Incluir desactivados</span>
      </label>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}
      {!loading && !error && ordenados.length === 0 && (
        <p className="text-sm text-text-muted">
          {incluirInactivos
            ? 'No hay entes externos registrados.'
            : 'No hay entes externos activos. Marca "Incluir desactivados" para ver el histórico.'}
        </p>
      )}
      {!loading && !error && ordenados.length > 0 && (
        <Table>
          <THead>
            <Th>Nombre</Th>
            <Th>Contacto</Th>
            <Th>Estado</Th>
            <Th>Acciones</Th>
          </THead>
          <TBody>
            {ordenados.map((e) => (
              <Tr key={e.id}>
                <Td>{e.nombre}</Td>
                <Td className="text-text-secondary">{e.contacto ?? '—'}</Td>
                <Td>
                  {e.activo ? (
                    <span className="text-state-success">Activo</span>
                  ) : (
                    <span className="text-text-muted">Desactivado</span>
                  )}
                </Td>
                <Td>
                  <div className="flex gap-2">
                    <ActionButton onClick={() => setRecursosDe(e)}>
                      Ver recursos
                    </ActionButton>
                    <ActionButton onClick={() => setEditando(e)}>
                      Editar
                    </ActionButton>
                    {e.activo && (
                      <ActionButton onClick={() => setRecursoDirectoDe(e)}>
                        + Recurso histórico
                      </ActionButton>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {creando && (
        <ModalForm
          inicial={null}
          onCerrar={() => setCreando(false)}
          onGuardado={() => {
            setCreando(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
      {editando && (
        <ModalForm
          inicial={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={() => {
            setEditando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
      {recursosDe && (
        <ModalRecursos
          ente={recursosDe}
          onCerrar={() => setRecursosDe(null)}
        />
      )}
      {recursoDirectoDe && (
        <ModalRecursoDirecto
          ente={recursoDirectoDe}
          onCerrar={() => setRecursoDirectoDe(null)}
          onGuardado={() => {
            setRecursoDirectoDe(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
